import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useFeatureFlags, useLoginByPassword } from '@repo/services'
import { generatePasswordInput } from '@repo/services/src/auth/password-tools'
import { EAnalyticsEventName, trackEvent } from '@repo/shared/adapters'
import { ButtonLink } from '@repo/shared/components'
import { useRouter } from '@repo/shared/i18n/navigation'
import { useFeatureFlagsStore } from '@repo/shared/store'
import { PasswordAttemptsManager } from '@repo/shared/utils'
import { Cookie } from '@repo/tools'
import {
  Button,
  Card,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  InputPassword,
  Typography,
} from '@repo/ui/components'
import { normalizeMobile, normalizeDigits } from '@repo/ui/lib'
import { useEffect, useState } from 'react'
import { useLoginSchema } from '../../schemas'
import { AxiosError, ILoginPasswordProps } from './login.types'

export const LoginPassword = (props: ILoginPasswordProps) => {
  const {
    identifier,
    handleBack,
    passwordData,
    onRedirectToOtp,
    redirectingUrl,
    isEmail,
  } = props
  const [isLoading, setIsLoading] = useState(false)
  const [hasExceededAttempts, setHasExceededAttempts] = useState<boolean>(false)

  const { errorHandler } = useErrorHandler()
  const { replace } = useRouter()
  const { t } = useTranslate()
  const { passwordSchema } = useLoginSchema()
  const featureFlagsQuery = useFeatureFlags()
  const { setFlags } = useFeatureFlagsStore()

  const { mutate: login } = useLoginByPassword()

  const { form } = useAppForm({
    formSchema: passwordSchema,
  })

  // Check password attempts on component mount
  useEffect(() => {
    const exceeded = PasswordAttemptsManager.hasReachedMaxAttempts(identifier)
    setHasExceededAttempts(exceeded)
  }, [identifier])

  const onSubmit = async (data: FormSchema<typeof passwordSchema>) => {
    // If user has exceeded attempts, redirect to OTP
    if (hasExceededAttempts) {
      onRedirectToOtp?.() // Redirect directly to OTP
      return
    }

    setIsLoading(true)
    const algo1 = {
      salt1: passwordData.accountPassword?.algorithmData?.salt1 ?? '',
      salt2: passwordData.accountPassword?.algorithmData?.salt2 ?? '',
      g: passwordData.accountPassword?.algorithmData?.g ?? 0,
      p: passwordData.accountPassword?.algorithmData?.p ?? '',
    }
    const randomData = passwordData.accountPassword?.randomData ?? ''
    const srpB = passwordData.accountPassword?.srpB ?? ''

    const password = btoa(normalizeDigits(data.password))

    const input = await generatePasswordInput(
      {
        SrpID: 1,
        SrpType: 'user',
        algorithmData: algo1,
        hasPassword: true,
        randomData: randomData,
        srpB: srpB,
      },
      password,
    )
    // Determine if identifier is email or mobile
    const isEmailIdentifier = isEmail || identifier.includes('@')

    login(
      isEmailIdentifier
        ? {
            email: identifier,
            a: input.a,
            m1: input.m1,
          }
        : {
            phone: normalizeMobile(identifier),
            a: input.a,
            m1: input.m1,
          },
      {
        onSuccess: async (res) => {
          // Clear password attempts on successful login
          PasswordAttemptsManager.clearAttempts()

          // Refetch feature flags after successful login
          featureFlagsQuery.refetch()
          if (featureFlagsQuery.data?.flags) {
            setFlags(featureFlagsQuery.data.flags)
          }
          trackEvent({
            name: EAnalyticsEventName.Login,
            properties: {
              user_id_custom: res.data.userID,
            },
          })

          // Clear redirecting URL and redirect to saved URL or home
          Cookie.clearRedirectingUrl()
          const targetUrl = redirectingUrl ?? '/'
          // Use window.location.replace to redirect and clear history
          if (typeof window !== 'undefined') {
            window.location.replace(targetUrl)
          } else {
            replace(targetUrl)
          }
        },
        onError: (error) => {
          setIsLoading(false)

          // Increment failed attempts
          PasswordAttemptsManager.incrementAttempts(identifier)

          const newExceeded =
            (error as unknown as AxiosError)?.response?.data?.item ===
              'TOO_MANY_ATTEMPTS' ||
            PasswordAttemptsManager.hasReachedMaxAttempts(identifier)

          setHasExceededAttempts(newExceeded)

          errorHandler(error, t)
        },
      },
    )
  }

  return (
    <Card className='p-xl rounded-sm'>
      <Col className='gap-3xl'>
        <Button
          startIcon={<Icon name='ArrowRightLine' />}
          variant='transparent'
          size='xs'
          onClick={handleBack}
          className='gap-2xs'
        >
          {t('common_back')}
        </Button>

        {/* Show attempt warnings */}
        {hasExceededAttempts ? (
          <Col className='gap-md'>
            <Typography
              size='sm'
              weight='bold'
              className='text-error-main text-center p-md bg-error-surface rounded-md'
            >
              {t('loginPage_passwordAttemptsExceeded')}
            </Typography>
            <Button
              className='w-full'
              variant='fill'
              size='lg'
              onClick={onRedirectToOtp}
            >
              {t('common_forgotPassword')}
            </Button>
          </Col>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-3xl w-full'
            >
              <FormField
                control={form.control}
                name='password'
                extra={
                  <ButtonLink
                    href='/auth/forgot-password'
                    size='xs'
                    color='base'
                    type='button'
                  >
                    {t('common_forgotPassword')}
                  </ButtonLink>
                }
                render={({ field }) => (
                  <FormItemWrapper
                    title={t('loginPage_password')}
                    inputSize='lg'
                  >
                    <InputPassword autoComplete='off' autoFocus {...field} />
                  </FormItemWrapper>
                )}
              />
              <Button
                className='w-full'
                type='submit'
                variant='fill'
                size='lg'
                loading={isLoading}
              >
                {t('common_login')}
              </Button>
            </form>
          </Form>
        )}
      </Col>
    </Card>
  )
}
